import React, { useEffect, useMemo, useRef, useState } from 'react'
import CommonInput from '../CommonInput/CommonInput.tsx'
import CommonInputType from '../../helpers/types/CommonInput.interface.ts'
import { makeStyles } from '@material-ui/core/styles'
import menuStyle from '../Menu/style.ts'
import Icon from '../Icon/Icon.tsx'
import style from './style.ts'
import { useSelectState } from './useSelectState.ts'
import SearchableSelectTrigger from './SearchableSelectTrigger'
import SearchableSelectMenu from './SearchableSelectMenu'

const useStyles = makeStyles(style)
const useMenuStyles = makeStyles(menuStyle)

type HtmlInputProps = Omit<React.HTMLProps<HTMLDivElement>, 'children' | 'onChange' | 'size'>
type Option = { value?: any; text?: React.ReactNode; title?: boolean; selected?: boolean; icon?: string; disabled?: boolean; renderComponent?: JSX.Element }

interface PropsInterface extends HtmlInputProps, CommonInputType {
    onClear?(): any
    onChange?(v: any): any
    options: Option[]
    icon?: React.ReactNode
    disabled?: boolean
    fullWidth?: boolean
    placeholder?: string
    searchPlaceholder?: string
    title?: string
    selectMultiple?: boolean
    returnObject?: boolean
    required?: boolean
    withIcon?: boolean
    onOpen?: () => void
}

/**
 *
 */
const SearchableSelect = ({
    size = 'large',
    labelPlacement,
    autoWidth,
    rules,
    isValid,
    helperText,
    onChange,
    options: initialOptions = [{ disabled: true, text: 'No data', value: null }],
    disabled = false,
    value,
    placeholder,
    searchPlaceholder = 'Search',
    title = 'Select an option',
    fullWidth = false,
    icon,
    selectMultiple = false,
    returnObject = false,
    required = false,
    withIcon,
    onOpen,
    className,
    'data-testid': dataTestId = 'searchable-select',
    ...props
}: PropsInterface): JSX.Element => {
    const [search, setSearch] = useState('')
    const menuRef = useRef<HTMLDivElement>(null)
    const {
        isOpen,
        setIsOpen,
        selectedValue,
        options,
        onSelect,
        toggleDropdown
    } = useSelectState({
        initialOptions,
        value,
        selectMultiple,
        required,
        returnObject,
        onChange
    })

    const classes = useStyles({ isOpen, selectedValue, selectedItemIcon: '', disabled })
    const menuClasses = useMenuStyles({ withIcon: false })
    const filteredOptions = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return options
        return options.filter((opt) => `${opt.text ?? ''}`.toLowerCase().includes(q))
    }, [options, search])

    const clearSearch = (event?: React.SyntheticEvent) => {
        event?.preventDefault()
        event?.stopPropagation()
        setSearch('')
    }

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setIsOpen(false)
        }
    }

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const renderMenu = () => (
        <SearchableSelectMenu
            dataTestId={dataTestId}
            filteredOptions={filteredOptions}
            menuClasses={menuClasses}
            search={search}
            searchPlaceholder={searchPlaceholder}
            clearSearch={clearSearch}
            setSearch={setSearch}
            onSelect={onSelect}
            selectedValue={selectedValue}
        />
    )

    return (
        <CommonInput
            value={(options && (
                !selectMultiple
                    ? (options.find(x => x.value === selectedValue)?.text || null)
                    : (Array.isArray(selectedValue) && selectedValue.length > 0 ? selectedValue : null)
            )) || null}
            key={'SearchableSelectInput'}
            rules={rules}
            isValid={isValid}
            helperText={helperText}
            size={size}
            fullWidth={fullWidth}
            className={className}
            label={props.label}
            labelPlacement={labelPlacement}
            autoWidth={autoWidth}
            data-testid={dataTestId}
            leadingIcon={props.leadingIcon}
            onClickLeadingIcon={props.onClickLeadingIcon}
            trailingIcon={props.trailingIcon}
            onClickTrailingIcon={props.onClickTrailingIcon}
            disabled={disabled}
        >
            <div className={`${classes.container} select ${isOpen && 'input--focused'}`} data-testid={dataTestId}>
                <div
                    onClick={() => toggleDropdown(disabled, onOpen)}
                    className={classes.selectConatiner}
                    data-testid={`${dataTestId}-select-toggle`}
                >
                    <SearchableSelectTrigger
                        icon={icon}
                        classes={classes}
                        disabled={disabled}
                        selectMultiple={selectMultiple}
                        selectedValue={selectedValue}
                        options={options}
                        placeholder={placeholder}
                        title={title}
                    />

                    <span className={classes.chevron} data-testid={`${dataTestId}-chevron`}>
                        {isOpen
                            ? <Icon name='menu-expanded' color={disabled ? 'neutral-300' : 'neutral-900'} size={24} />
                            : <Icon name='menu-collapsed' color={disabled ? 'neutral-300' : 'neutral-900'} size={24} />}
                    </span>
                </div>

                <div ref={menuRef}>
                    {isOpen && !disabled && renderMenu()}
                </div>
            </div>
        </CommonInput>
    )
}

/**
 *
 */
export type SearchableSelectType = PropsInterface
/**
 *
 */
export type SearchableSelectOptionType = Option

export default SearchableSelect
